import { DestroyRef, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationItem, NavigationService } from '@core/services/navigation.service';
import { RealtimeEventPayload, RealtimeService } from '@core/services/realtime/realtime.service';
import { RealtimeEventType } from '@flux/core/client';
import { BehaviorSubject, filter } from 'rxjs';

export type SidebarItem = NavigationItem;

export interface SidebarConfig {
  title: string;
  items: SidebarItem[];
  logoUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  public config: WritableSignal<SidebarConfig> = signal({
    title: 'FluxControl',
    items: [],
  });

  public collapsed: WritableSignal<boolean> = signal(true);

  public notificationsCount$ = new BehaviorSubject<number>(0);

  constructor(
    private readonly navigationService: NavigationService,
    private readonly realtimeService: RealtimeService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.setConfig(this.config());
    this.listenToNotifications();
  }

  public setConfig(config: SidebarConfig): void {
    const items = this.navigationService.getNavigationItems();

    this.config.set({
      ...config,
      items,
    });
  }

  public toggleCollapse(): void {
    this.collapsed.set(!this.collapsed());
  }

  private listenToNotifications(): void {
    this.realtimeService.eventReceived$
      .pipe(
        filter(
          (event): event is RealtimeEventPayload<RealtimeEventType.NotificationCreate> =>
            event.op === RealtimeEventType.NotificationCreate,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ data }) => {
        this.notificationsCount$.next(data.data.count);
      });
  }
}
